import { ProcessedRequest, RequestStatus, UserInfo } from "../domain/ProcessedRequest";
import { ProcessedRequestRepo } from "../domain/ProcessedRequestRepo";
import { query_dispatcher } from "@/api/client";


export class APIProcessedRequestRepository implements ProcessedRequestRepo {
  async find_all(limit: number = 50, offset: number = 0): Promise<ProcessedRequest[]> {
    const results: ProcessedRequest[] = [];

    // Get ausencias procesadas
    try {
      const absences = await this.fetchProcessedAbsences();
      results.push(...absences);
    } catch (error) {
      console.error('Error loading processed absences:', error);
    }

    // 2. Get modificaciones de horario procesadas
    try {
      const clockModifications = await this.fetchProcessedClockModifications();
      results.push(...clockModifications);
    } catch (error) {
      console.error('Error loading processed clock modifications:', error);
    }

    // Fecha de resolución
    results.sort((a, b) => {
      const dateA = a.getResolutionDate() || '';
      const dateB = b.getResolutionDate() || '';
      return dateB.localeCompare(dateA);
    });

    // Paginación
    return results.slice(offset, offset + limit);
  }

  
  async find_by_type(type: 'absence' | 'clock_modification', limit: number = 50, offset: number = 0): Promise<ProcessedRequest[]> {
    let results: ProcessedRequest[] = [];

    if (type === 'absence') {
      results = await this.fetchProcessedAbsences();
    } else {
      results = await this.fetchProcessedClockModifications();
    }

    
    results.sort((a, b) => {
      const dateA = a.getResolutionDate() || '';
      const dateB = b.getResolutionDate() || '';
      return dateB.localeCompare(dateA);
    });

    return results.slice(offset, offset + limit);
  }

  
  async find_by_status(status: 'approved' | 'rejected', limit: number = 50, offset: number = 0): Promise<ProcessedRequest[]> {
    const allResults = await this.find_all(1000, 0);

    const filtered = allResults.filter(req => {
      if (status === 'approved') {
        return req.isApproved();
      }
      return req.isRejected();
    });

    return filtered.slice(offset, offset + limit);
  }

  
  private async fetchProcessedAbsences(): Promise<ProcessedRequest[]> {
    const results: ProcessedRequest[] = [];

    
    try {
      const approvedResponse = await query_dispatcher.dispatch('filter_absences.query', {
        filters: [['status', '==', 'approved']]
      });

      if (Array.isArray(approvedResponse)) {
        results.push(...approvedResponse.map((a: any) => this.mapAbsenceToProcessedRequest(a, 'approved')));
      }
    } catch (e) {
      console.error('Error fetching approved absences:', e);
    }

    
    try {
      const rejectedResponse = await query_dispatcher.dispatch('filter_absences.query', {
        filters: [['status', '==', 'rejected']]
      });

      if (Array.isArray(rejectedResponse)) {
        results.push(...rejectedResponse.map((a: any) => this.mapAbsenceToProcessedRequest(a, 'rejected')));
      }
    } catch (e) {
      console.error('Error fetching rejected absences:', e);
    }

    return results;
  }

  
  private async fetchProcessedClockModifications(): Promise<ProcessedRequest[]> {
    const results: ProcessedRequest[] = [];

    try {
      
      const response = await query_dispatcher.dispatch('filter_clocks.query', {
        filters: [],
        limit: 500
      });

      if (Array.isArray(response)) {
        for (const clock of response) {
          if (clock.changes && Array.isArray(clock.changes)) {
            
            for (let idx = 1; idx < clock.changes.length; idx++) {
              const change = clock.changes[idx];
              if (change.state === 'validated' || change.state === 'rejected') {
                results.push(this.mapClockChangeToProcessedRequest(clock, change, idx));
              }
            }
          }
        }
      }
    } catch (e) {
      console.error('Error fetching processed clock modifications:', e);
    }

    return results;
  }

  
  private mapAbsenceToProcessedRequest(absence: any, status: RequestStatus): ProcessedRequest {
    const userInfo: UserInfo = absence.user_info || {
      id: absence.user_id,
      username: absence.username || 'Desconocido'
    };

    
    let resolutionInfo = null;
    if (absence.changes && Array.isArray(absence.changes)) {
      for (let i = absence.changes.length - 1; i >= 0; i--) {
        const change = absence.changes[i];
        if (change.action === 'approved' || change.action === 'rejected') {
          resolutionInfo = {
            resolved_at: change.timestamp,
            resolved_by: change.user,
            action: change.action,
            details: change.details
          };
          break;
        }
      }
    }

    return new ProcessedRequest({
      uuid: absence.uuid,
      request_type: 'absence',
      user_id: absence.user_id,
      user_info: userInfo,
      status: status,
      resolution_info: resolutionInfo,
      start: absence.start,
      end: absence.end,
      reason: absence.reason,
      tipoAusencia: absence.tipoAusencia || absence.tipo_ausencia
    });
  }

  
  private mapClockChangeToProcessedRequest(clock: any, change: any, changeIndex: number): ProcessedRequest {
    const userInfo: UserInfo = clock.user_info || {
      id: clock.user_id,
      username: 'Desconocido'
    };

    const status: RequestStatus = change.state === 'validated' ? 'approved' : 'rejected';

    return new ProcessedRequest({
      uuid: `${clock.uuid}_change_${changeIndex}`,
      request_type: 'clock_modification',
      user_id: clock.user_id,
      user_info: userInfo,
      status: status,
      resolution_info: {
        resolved_at: change.approval_date || null,
        resolved_by: change.admin_uuid || null,
        action: status === 'approved' ? 'approved' : 'rejected'
      },
      original_timestamp: clock.changes[0]?.timestamp,
      proposed_timestamp: change.timestamp,
      clock_type: change.type,
      justification: change.justification,
      approval_date: change.approval_date
    });
  }
}
